import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Send, CheckCircle2, Loader2, Download, Wallet } from "lucide-react";
import { generateBoxInvoicePdf } from "../lib/invoicePdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const customerApi = axios.create({ baseURL: API });
customerApi.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("heritage-customer-token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const STATUS_LABEL = {
  awaiting_payment: "Awaiting payment, your invoice is ready (48h window)",
  submitted: "Submitted, Heritage will confirm and pack",
  invoiced: "Invoice ready, please pay within 48h",
  paid: "Paid, your mystery box is locked in",
  allocated: "Allocated",
  fulfilling: "Being packed",
  shipped: "Shipped",
  cancelled: "Cancelled",
  expired: "Expired (payment window passed)",
};

/**
 * Heritage HQ , VIP Mystery Box (Feb 2026 rebuild).
 *
 * Three tiers (Beginner / Hobby / Addict). On submit, the invoice is
 * generated immediately, a 48h payment window starts, and the order
 * cascades into the Console pipeline at 'awaiting_payment'.
 */
export default function VipMysteryBox({ embedded = false } = {}) {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [order, setOrder] = useState(null);
  const [credits, setCredits] = useState(0);
  const [applyCredit, setApplyCredit] = useState(false);
  const [tierKey, setTierKey] = useState("hobby");
  const [love, setLove] = useState("");
  const [avoid, setAvoid] = useState("");
  const [special, setSpecial] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await customerApi.get("/customer/vip-mystery-box");
        setTiers(r.data?.tiers || []);
        if (r.data?.order) setOrder(r.data.order);
        setCredits(Number(r.data?.heritage_credits || 0));
      } catch (e) {
        if (e?.response?.status === 401) navigate("/account/login");
        else if (e?.response?.status === 403)
          toast.error(e?.response?.data?.detail || "VIP Mystery Box isn't enabled on your account.");
        else toast.error("Couldn't load the mystery box.");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const tier = useMemo(
    () => tiers.find((t) => t.key === tierKey) || tiers[0] || { key: tierKey, size: 0, price: 0, total: 0, label: "" },
    [tiers, tierKey],
  );

  const creditEligible = Math.min(credits, Number(tier.price || 0));
  const willApply = applyCredit ? creditEligible : 0;
  const finalTotal = Math.max(0, Number(tier.price || 0) - willApply);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await customerApi.post("/customer/vip-mystery-box", {
        tier: tier.key,
        love: love.trim(),
        avoid: avoid.trim(),
        special: special.trim(),
        apply_credit: applyCredit,
      });
      setOrder(r.data);
      setCredits((prev) => Math.max(0, prev - Number(r.data.credit_applied || 0)));
      toast.success("Mystery box submitted. You have 48 hours to pay your invoice.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] grid place-items-center text-[var(--dahlias-ink)]/60" data-testid="mystery-loading">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const locked = order && !["cancelled", "expired"].includes(order.status);

  return (
    <div className="space-y-7" data-testid="vip-mystery-box">
      <section className="bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-forest)]/25 wobble-a p-6 md:p-8">
        <span className="font-label tracking-[0.4em] text-[10px] uppercase text-[var(--dahlias-forest)]">
          ✦ HERITAGE HQ , EARLY ACCESS ✦
        </span>
        <h1 className="mt-2 font-display text-3xl md:text-4xl text-[var(--dahlias-forest)] leading-tight flex items-center gap-3">
          Heritage Mystery Box
          <Sparkles className="w-7 h-7 text-[var(--berry)]" />
        </h1>
        <p className="mt-4 text-base text-[var(--dahlias-ink)]/85 leading-relaxed max-w-2xl">
          Love a surprise? Our Heritage Mystery Box is packed with a hand-selected collection
          of beautiful heritage treasures chosen from our collection. You may discover rare
          favourites, timeless classics, and hidden gems, with every box offering a unique
          mix of varieties. Perfect for collectors, gardeners who enjoy a little excitement,
          or anyone looking to expand their garden with something special.
        </p>
        <p className="mt-3 text-base italic text-[var(--berry-deep)] font-display-italic">
          Every box is different, making each unboxing a delightful garden adventure.
        </p>
      </section>

      {locked ? (
        <ExistingOrderCard order={order} />
      ) : (
        <>
          <section data-testid="mystery-tiers" className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {tiers.map((t) => {
              const active = t.key === tier.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTierKey(t.key)}
                  data-testid={`mystery-tier-${t.key}`}
                  className={`text-left p-6 wobble-a border-[3px] transition ${
                    active
                      ? "bg-[var(--dahlias-forest)] text-[#FBE6D8] border-[var(--dahlias-sage-deep)]"
                      : "bg-[var(--dahlias-paper)] text-[var(--dahlias-ink)] border-[var(--dahlias-sage)]/30 hover:border-[var(--dahlias-forest)]/60"
                  }`}
                >
                  <div
                    className={`font-label tracking-[0.4em] text-[10px] uppercase ${
                      active ? "text-[#FBE6D8]/80" : "text-[var(--berry)]"
                    }`}
                  >
                    {t.label}
                  </div>
                  <div className="mt-2 font-display text-3xl">
                    {t.size}+ mysteries
                  </div>
                  <div
                    className={`mt-1 font-display-italic text-2xl ${
                      active ? "text-[#FBE6D8]" : "text-[var(--berry)]"
                    }`}
                  >
                    NZ${Number(t.price).toFixed(0)}
                  </div>
                  <div
                    className={`mt-3 text-xs ${
                      active ? "text-[#FBE6D8]/80" : "text-[var(--dahlias-ink)]/70"
                    }`}
                  >
                    Heritage chooses every tuber. Delivered NZ-wide.
                  </div>
                  {active && (
                    <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-label tracking-[0.3em] uppercase">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Selected
                    </div>
                  )}
                </button>
              );
            })}
          </section>

          {credits > 0 && (
            <section
              data-testid="mystery-credit-row"
              className="bg-[var(--berry)]/8 border-2 border-[var(--berry)]/30 wobble-a p-4 md:p-5 flex flex-wrap items-center justify-between gap-3"
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[var(--berry)] text-white">
                  <Wallet className="w-4 h-4" />
                </span>
                <div>
                  <div className="font-label tracking-[0.3em] text-[10px] uppercase text-[var(--berry-deep)]">
                    Heritage Credit balance
                  </div>
                  <div className="font-display text-xl text-[var(--dahlias-forest)]">
                    NZ${credits.toFixed(2)} available
                  </div>
                </div>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-[var(--dahlias-ink)] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={applyCredit}
                  onChange={(e) => setApplyCredit(e.target.checked)}
                  data-testid="mystery-apply-credit"
                  className="w-4 h-4 accent-[var(--berry)]"
                />
                Apply <strong className="text-[var(--berry-deep)]">NZ${creditEligible.toFixed(2)}</strong> to this box
              </label>
            </section>
          )}

          <section className="bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/30 wobble-a p-5 md:p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <NoteField
                id="mystery-love" testid="mystery-love"
                label="Love"
                placeholder="e.g. Purples, oranges, peaches"
                value={love} onChange={setLove}
              />
              <NoteField
                id="mystery-avoid" testid="mystery-avoid"
                label="Avoid"
                placeholder="e.g. White, plain red"
                value={avoid} onChange={setAvoid}
              />
              <NoteField
                id="mystery-special" testid="mystery-special"
                label="Special requests"
                placeholder="e.g. Pom-pom shapes only"
                value={special} onChange={setSpecial}
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-[var(--dahlias-sage)]/20">
              <div>
                <div className="font-display text-2xl text-[var(--dahlias-forest)]">
                  {tier.label} ,{" "}
                  {willApply > 0 && (
                    <span className="text-[var(--dahlias-ink)]/40 line-through mr-1 text-xl">NZ${Number(tier.price).toFixed(0)}</span>
                  )}
                  <span className="text-[var(--berry)]" data-testid="mystery-final-total">NZ${finalTotal.toFixed(0)}</span>
                </div>
                <div className="text-xs text-[var(--dahlias-ink)]/60 mt-1">
                  {willApply > 0
                    ? `NZ$${willApply.toFixed(2)} Heritage Credit applied. Pay the balance via the downloadable invoice. 48h window.`
                    : "After submit, your invoice is ready and you have 48 hours to pay."}
                </div>
              </div>
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                data-testid="mystery-submit"
                className="h-12 px-6 inline-flex items-center gap-2 wobble-pill bg-[var(--berry)] hover:bg-[var(--berry-deep)] text-white text-sm font-label tracking-[0.25em] uppercase disabled:opacity-60"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submitting ? "Sending…" : "Submit Mystery Box"}
              </button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function NoteField({ id, testid, label, placeholder, value, onChange }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="font-label tracking-[0.3em] text-[10px] uppercase text-[var(--dahlias-sage-deep)]"
      >
        {label}
      </label>
      <textarea
        id={id}
        data-testid={testid}
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-2 w-full p-3 wobble-a bg-[var(--dahlias-cream)] border border-[var(--dahlias-sage)]/30 text-sm leading-relaxed"
      />
    </div>
  );
}

function MysteryPaymentCountdown({ deadline }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => { clearInterval(id); };
  }, []);
  const due = new Date(deadline).getTime();
  if (Number.isNaN(due)) return null;
  const msLeft = due - now;
  if (msLeft <= 0) {
    return (
      <div data-testid="mystery-payment-expired" className="mt-3 text-xs font-label tracking-[0.25em] uppercase text-[#A33030]">
        Payment window has expired
      </div>
    );
  }
  const hh = Math.floor(msLeft / 3600000);
  const mm = Math.floor((msLeft % 3600000) / 60000);
  return (
    <div data-testid="mystery-payment-countdown" className="mt-3 text-xs font-label tracking-[0.25em] uppercase text-[#A33030]">
      {hh}h {mm.toString().padStart(2, "0")}m left to pay your invoice
    </div>
  );
}

function ExistingOrderCard({ order }) {
  const downloadInvoice = () => {
    try {
      const today = new Date(order.invoice_sent_at || order.submitted_at || Date.now());
      const due = new Date(order.payment_due_at || (today.getTime() + 48 * 3600 * 1000));
      const iso = (d) => new Date(d).toISOString().slice(0, 10);
      const subtotal = Number(order.price || 0);
      const creditApplied = Number(order.credit_applied || 0);
      const total = Number(order.total ?? Math.max(0, subtotal - creditApplied));
      generateBoxInvoicePdf({
        invoiceNumber: order.invoice_number || `HD-${String(order.id || "").replace(/-/g, "").slice(0, 6).toUpperCase()}`,
        issueDate: iso(today),
        dueDate: iso(due),
        customer: { name: order.customer_name, email: order.customer_email },
        items: [{
          description: `${order.label || "Heritage Mystery Box"} , ${order.size}+ hand-picked heritage tubers`,
          qty: 1,
          unit_price: subtotal,
        }],
        subtotal,
        creditApplied,
        total,
        notes: [order.love && `LOVE: ${order.love}`, order.avoid && `AVOID: ${order.avoid}`, order.special && `SPECIAL: ${order.special}`].filter(Boolean).join("  •  "),
      });
      toast.success("Invoice downloaded.");
    } catch (e) {
      toast.error("Couldn't generate the invoice PDF.");
    }
  };
  return (
    <section
      data-testid="mystery-existing-order"
      className="bg-[var(--dahlias-paper)] border-2 border-[var(--berry)]/30 wobble-a p-6 md:p-7"
    >
      <span className="font-label tracking-[0.4em] text-[10px] uppercase text-[var(--berry)]">
        ✦ YOUR MYSTERY BOX ✦
      </span>
      <h2 className="mt-2 font-display text-2xl md:text-3xl text-[var(--dahlias-forest)]">
        {order.label || `${order.size} mysteries`} , NZ${Number(order.total ?? order.price).toFixed(0)}
      </h2>
      {Number(order.credit_applied || 0) > 0 && (
        <p className="mt-1 text-sm text-[var(--berry)]">
          Heritage Credit applied: NZ${Number(order.credit_applied).toFixed(2)} (off the NZ${Number(order.price).toFixed(0)} list price)
        </p>
      )}
      <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 wobble-pill bg-[var(--dahlias-forest)]/10 border border-[var(--dahlias-forest)]/30">
        <span className="font-label tracking-[0.3em] text-[10px] uppercase text-[var(--dahlias-forest)]">
          Status
        </span>
        <span className="text-sm text-[var(--dahlias-forest)]" data-testid="mystery-status-label">
          {STATUS_LABEL[order.status] || order.status}
        </span>
      </div>
      {order.status === "awaiting_payment" && order.payment_due_at && (
        <MysteryPaymentCountdown deadline={order.payment_due_at} />
      )}
      {(order.love || order.avoid || order.special) && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          {order.love && (
            <div className="p-3 bg-[#FBF6E9] border border-[#E8DFCB] rounded">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E1E3A]">Love</div>
              <div className="mt-1 italic text-[var(--dahlias-ink)]/85">{order.love}</div>
            </div>
          )}
          {order.avoid && (
            <div className="p-3 bg-[#FBF6E9] border border-[#E8DFCB] rounded">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E1E3A]">Avoid</div>
              <div className="mt-1 italic text-[var(--dahlias-ink)]/85">{order.avoid}</div>
            </div>
          )}
          {order.special && (
            <div className="p-3 bg-[#FBF6E9] border border-[#E8DFCB] rounded">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E1E3A]">Special</div>
              <div className="mt-1 italic text-[var(--dahlias-ink)]/85">{order.special}</div>
            </div>
          )}
        </div>
      )}
      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={downloadInvoice}
          data-testid="mystery-download-invoice"
          className="inline-flex items-center gap-2 h-11 px-5 wobble-pill bg-[var(--berry)] hover:bg-[var(--berry-deep)] text-white text-xs font-label tracking-[0.25em] uppercase"
        >
          <Download className="w-3.5 h-3.5" /> Download invoice
        </button>
      </div>
      <p className="mt-4 text-sm text-[var(--dahlias-ink)]/70 leading-relaxed">
        Heritage has your order. We&apos;ll confirm allocation once payment is received.
        If you need to change anything, message us via Heritage Chat.
      </p>
    </section>
  );
}
